/**
 * AddCustomOption Component
 *
 * Clickable row that lets users add a freeform custom entry when
 * the search term doesn't match existing Chariot records (or when
 * the user simply wants something different from the results).
 *
 * Appears between the search input and results area whenever the
 * user has typed a non-empty search term.
 */

import { PlusCircleIcon } from '@heroicons/react/24/outline';
import type { KeyboardEvent } from 'react';

import { sanitizeInput } from '@/sections/attacks/attackPaths/components/wizards/types';
import { cn } from '@/utils/classname';

/**
 * Props for AddCustomOption component
 */
export interface AddCustomOptionProps {
  /** Raw search term typed by the user */
  searchTerm: string;
  /** Called with the sanitized term when the user confirms the custom entry */
  onAddCustom: (sanitizedTerm: string) => void;
  /** Entity noun used in the label, e.g. "technique", "asset", "vulnerability" */
  entityName: string;
  /** Optional test-ID prefix (defaults to "add-custom-option") */
  testIdPrefix?: string;
}

/**
 * Renders a clickable "Add X as custom Y" row.
 *
 * Returns null when the search term is empty or sanitizes to empty,
 * so callers never need a guard.
 *
 * @example
 * ```tsx
 * <AddCustomOption
 *   searchTerm={searchValue}
 *   onAddCustom={handleAddCustom}
 *   entityName="technique"
 * />
 * ```
 */
export function AddCustomOption({
  searchTerm,
  onAddCustom,
  entityName,
  testIdPrefix = 'add-custom-option',
}: AddCustomOptionProps) {
  const sanitized = sanitizeInput(searchTerm);

  // Only render when both raw and sanitized values are non-empty
  if (!searchTerm.trim() || !sanitized) return null;

  function handleClick() {
    onAddCustom(sanitized);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onAddCustom(sanitized);
    }
  }

  return (
    <div
      role="button"
      aria-label={`Add "${sanitized}" as custom ${entityName}`}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'border-chariot-stroke bg-chariot-background rounded-lg border px-4 py-3',
        'flex cursor-pointer items-center gap-3',
        'hover:bg-chariot-background-secondary',
        'focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
      )}
      data-testid={`${testIdPrefix}-row`}
    >
      <PlusCircleIcon className="text-chariot-brand-primary size-5 shrink-0" />
      <p
        className="text-chariot-text-primary truncate text-sm"
        title={`Add "${sanitized}" as custom ${entityName}`}
      >
        Add &ldquo;
        <span className="font-medium">{sanitized}</span>
        &rdquo; as custom {entityName}
      </p>
    </div>
  );
}
